import React, { useCallback, useEffect, useMemo, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import useSubscriptionPlans from "@/hooks/useSubscriptionPlans";
import {
  ArrowLeft, Building2, Users as UsersIcon, MapPin, Phone, Mail, Calendar, Activity,
  CheckCircle2, PauseCircle, Archive, Loader2, Crown, Database, BarChart3, Key, LogIn,
  AlertTriangle, Clock, RefreshCw, Edit3, Save, X, Infinity as InfinityIcon,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

// Normalise FastAPI / axios error payloads. Pydantic 422 responses ship
// `detail` as an array of `{type, loc, msg, ...}` objects which crashes
// React when handed to <Toaster> directly.
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Error";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d
      .map((it) => {
        if (typeof it === "string") return it;
        const where = Array.isArray(it?.loc) ? it.loc.slice(-1)[0] : null;
        return where ? `${where}: ${it.msg}` : it?.msg || JSON.stringify(it);
      })
      .join(" · ");
  }
  return typeof d === "object" ? (d.msg || JSON.stringify(d)) : String(d);
};

const STATUS_STYLES = {
  trial:     { bg: "bg-amber-100",  text: "text-amber-700",  label: { ar: "تجريبي",   en: "Trial" }, Icon: Clock },
  active:    { bg: "bg-emerald-100", text: "text-emerald-700", label: { ar: "نشط",      en: "Active" }, Icon: CheckCircle2 },
  suspended: { bg: "bg-rose-100",    text: "text-rose-700",    label: { ar: "موقوف",    en: "Suspended" }, Icon: PauseCircle },
  expired:   { bg: "bg-orange-100",  text: "text-orange-700",  label: { ar: "منتهي",    en: "Expired" }, Icon: AlertTriangle },
  archived:  { bg: "bg-stone-200",   text: "text-stone-600",   label: { ar: "مؤرشف",    en: "Archived" }, Icon: Archive },
};

const StatusBadge = ({ status, lang }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.active;
  const Ico = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
      <Ico size={12} />{s.label[lang === "ar" ? "ar" : "en"]}
    </span>
  );
};

// Quick-action button used in the right column. Kept inline to avoid a new
// dependency / component file for what is a one-page surface.
const ActionBtn = ({ Icon, label, onClick, variant = "default", "data-testid": tid }) => {
  const cls = {
    default: "bg-stone-50 hover:bg-stone-100 text-stone-700 border-stone-200",
    danger:  "bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-200",
    success: "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200",
    gold:    "bg-[var(--gold-tint)] hover:bg-[var(--gold-tint-strong)] text-[var(--gold)] border-[var(--gold)]/30",
  }[variant];
  return (
    <button onClick={onClick} data-testid={tid}
      className={`w-full text-start text-sm px-3 py-2.5 rounded-xl border flex items-center gap-2.5 transition ${cls}`}>
      <Icon size={15}/>{label}
    </button>
  );
};

const Card = ({ title, Icon, children, action }) => (
  <div className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
    <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between bg-stone-50/40">
      <div className="flex items-center gap-2 text-stone-700 font-semibold text-sm"><Icon size={14}/>{title}</div>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Row = ({ label, value, mono }) => (
  <div className="flex items-center justify-between text-sm py-1.5">
    <span className="text-stone-500 text-xs">{label}</span>
    <span className={`text-stone-800 ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</span>
  </div>
);

const ProgressBar = ({ value, max, color = "var(--gold)" }) => {
  // `max=null` means unlimited (∞). Show a flat zero-progress bar with an
  // infinity label so the operator sees "10 / ∞" instead of a phantom
  // percentage.
  if (max === null || max === undefined) {
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between text-[11px] text-stone-500">
          <span className="num-display">{value} / <span className="text-emerald-600 font-semibold">∞</span></span>
          <span className="text-[10px] text-emerald-600 font-semibold">UNLIMITED</span>
        </div>
        <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `8%`, background: "repeating-linear-gradient(45deg,#a7f3d0,#a7f3d0 6px,#d1fae5 6px,#d1fae5 12px)" }}/>
        </div>
      </div>
    );
  }
  const pct = Math.min(100, Math.round((value / Math.max(max || 1, 1)) * 100));
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[11px] text-stone-500">
        <span className="num-display">{value} / {max}</span>
        <span className="num-display">{pct}%</span>
      </div>
      <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }}/>
      </div>
    </div>
  );
};

// Professional plan-switch modal — dropdown of active plans + start/end
// dates + quick-period presets + optional reason. Submits to
// POST /admin/tenants/{tid}/change-plan and refreshes the parent.
const ChangePlanModal = ({ open, tenant, onClose, onSaved, lang }) => {
  const isAr = lang === "ar";
  const { plans, loading } = useSubscriptionPlans({ activeOnly: true });

  const todayIso = () => new Date().toISOString().slice(0, 10);
  const plusMonths = (n) => {
    const d = new Date();
    d.setMonth(d.getMonth() + n);
    return d.toISOString().slice(0, 10);
  };

  const [plan, setPlan] = useState(tenant?.plan || "trial");
  const [startsAt, setStartsAt] = useState(todayIso());
  const [endsAt, setEndsAt] = useState(plusMonths(1));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && tenant) {
      setPlan(tenant.plan || (plans[0]?.code) || "trial");
      setStartsAt(todayIso());
      setEndsAt(plusMonths(1));
      setReason("");
    }
  }, [open, tenant, plans]);

  if (!open) return null;

  const selectedPlan = plans.find(p => p.code === plan);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!plan) return toast.error(isAr ? "اختر الخطة" : "Select a plan");
    if (!endsAt) return toast.error(isAr ? "حدد تاريخ النهاية" : "Set the end date");
    if (new Date(endsAt) <= new Date(startsAt)) {
      return toast.error(isAr ? "تاريخ النهاية يجب أن يكون بعد البداية" : "End must be after start");
    }
    setSaving(true);
    try {
      await api.post(`/admin/tenants/${tenant.tenant_id}/change-plan`, {
        plan,
        starts_at: new Date(startsAt + "T00:00:00Z").toISOString(),
        ends_at:   new Date(endsAt   + "T23:59:59Z").toISOString(),
        reason: reason || undefined,
      });
      toast.success(isAr ? "تم تغيير الخطة" : "Plan changed");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const fmtLimit = (v) => v === null || v === undefined
    ? <InfinityIcon size={11} className="inline text-emerald-600"/>
    : v;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, onClose)}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl" onMouseDown={onModalPanelMouseDown} data-testid="change-plan-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-xl gold-text">{isAr ? "تغيير الخطة" : "Change Plan"}</h2>
            <p className="text-xs text-stone-500 mt-0.5">{(tenant?.name || {}).en || tenant?.slug}</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X size={20}/></button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs text-stone-500">{isAr ? "الخطة" : "Plan"}</label>
            <select className="input-dark mt-1" value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="cp-plan">
              {loading && <option value="">…</option>}
              {plans.map(p => (
                <option key={p.code} value={p.code}>
                  {p.name_en} — {p.monthly_price} {p.currency || "QAR"}/mo
                </option>
              ))}
            </select>
            {selectedPlan && (
              <div className="text-[11px] text-stone-500 mt-1.5 flex flex-wrap gap-2">
                <span>{isAr ? "فروع" : "branches"}: <b className="text-stone-800">{fmtLimit(selectedPlan.max_branches)}</b></span>
                <span>· {isAr ? "مستخدمين" : "users"}: <b className="text-stone-800">{fmtLimit(selectedPlan.max_users)}</b></span>
                <span>· {isAr ? "خدمات" : "services"}: <b className="text-stone-800">{fmtLimit(selectedPlan.max_services)}</b></span>
                <span>· {isAr ? "حجز/شهر" : "bookings/mo"}: <b className="text-stone-800">{fmtLimit(selectedPlan.max_bookings_per_month)}</b></span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-stone-500">{isAr ? "تاريخ البداية" : "Start date"}</label>
              <input type="date" className="input-dark mt-1" value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)} required data-testid="cp-start"/>
            </div>
            <div>
              <label className="text-xs text-stone-500">{isAr ? "تاريخ النهاية" : "End date"}</label>
              <input type="date" className="input-dark mt-1" value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)} required min={startsAt} data-testid="cp-end"/>
            </div>
          </div>

          {/* Quick presets — set end date relative to start */}
          <div className="flex flex-wrap gap-1.5">
            {[
              { label: isAr ? "شهر" : "1 month", months: 1 },
              { label: isAr ? "٣ أشهر" : "3 months", months: 3 },
              { label: isAr ? "٦ أشهر" : "6 months", months: 6 },
              { label: isAr ? "سنة" : "12 months", months: 12 },
            ].map(p => (
              <button key={p.months} type="button" onClick={() => {
                const d = new Date(startsAt + "T00:00:00Z");
                d.setMonth(d.getMonth() + p.months);
                setEndsAt(d.toISOString().slice(0, 10));
              }} className="text-[11px] px-2.5 py-1 rounded-full border border-stone-200 hover:bg-[var(--gold-tint)] hover:text-[var(--gold)] hover:border-[var(--gold)] transition" data-testid={`cp-preset-${p.months}m`}>
                {p.label}
              </button>
            ))}
          </div>

          <div>
            <label className="text-xs text-stone-500">{isAr ? "السبب / ملاحظات (اختياري)" : "Reason / notes (optional)"}</label>
            <textarea rows={2} className="input-dark mt-1" value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={isAr ? "ترقية بعد دفع الفاتورة #..." : "Upgrade after invoice #..."}
              data-testid="cp-reason"/>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-[11px] text-amber-800 flex items-start gap-2">
            <AlertTriangle size={13} className="flex-shrink-0 mt-0.5"/>
            <span>
              {isAr
                ? "النظام يقفل تلقائياً عند تجاوز تاريخ النهاية — لن يستطيع المستأجر تسجيل الدخول حتى التجديد."
                : "The system will auto-lock at end date — tenant cannot log in until renewed."}
            </span>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-100">{isAr ? "إلغاء" : "Cancel"}</button>
            <button type="submit" disabled={saving} className="btn-gold px-5 py-2 rounded-lg flex items-center gap-2" data-testid="cp-save">
              {saving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>}
              {isAr ? "تطبيق" : "Apply"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function TenantDetail() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const loc = useLocation();
  const { lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const isAr = lang === "ar";
  const backPath = loc.pathname.startsWith("/platform-admin")
    ? "/platform-admin/tenants"
    : "/admin/tenants";

  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [changePlanOpen, setChangePlanOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/tenants/${tenantId}`);
      setTenant(data);
      setEditForm({
        name: (data.name || {}).en || "",
        name_ar: (data.name || {}).ar || "",
        owner_name: (data.contact || {}).owner_name || "",
        owner_email: (data.contact || {}).owner_email || "",
        owner_phone: (data.contact || {}).owner_phone || "",
        country: data.country || "",
        plan: data.plan || "trial",
        status: data.status || "active",
      });
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (payload, successMsg) => {
    try {
      await api.patch(`/admin/tenants/${tenantId}`, payload);
      toast.success(successMsg || (isAr ? "تم الحفظ" : "Saved"));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const action = async (path, body, successMsg) => {
    try {
      await api.post(`/admin/tenants/${tenantId}${path}`, body || {});
      toast.success(successMsg);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const onSuspend = async () => {
    const ok = await confirm({
      title: isAr ? "تعليق هذا Tenant؟" : "Suspend this tenant?",
      confirmLabel: isAr ? "تعليق" : "Suspend",
      cancelLabel: isAr ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    action("/suspend", null, isAr ? "تم التعليق" : "Suspended");
  };
  const onReactivate = () => action("/reactivate", null, isAr ? "تم التفعيل" : "Reactivated");
  const onArchive = async () => {
    const ok = await confirm({
      title: isAr ? "أرشفة Tenant؟ يخفيه من القوائم الأساسية." : "Archive tenant?",
      confirmLabel: isAr ? "أرشفة" : "Archive",
      cancelLabel: isAr ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    action("/archive", null, isAr ? "تمت الأرشفة" : "Archived");
  };

  const onExtendTrial = async () => {
    const days = window.prompt(isAr ? "كم يوماً إضافياً؟" : "How many days?", "14");
    if (!days) return;
    patch({ trial_extend_days: parseInt(days) }, isAr ? "تم تمديد التجربة" : "Trial extended");
  };

  const onChangePlan = () => setChangePlanOpen(true);

  const onResetPassword = async () => {
    const pwd = window.prompt(isAr ? "كلمة المرور الجديدة (6 أحرف على الأقل):" : "New password (min 6 chars):");
    if (!pwd || pwd.length < 6) return;
    try {
      await api.post(`/admin/tenants/${tenantId}/owner/reset-password`, { new_password: pwd });
      toast.success(isAr ? "تم تغيير كلمة المرور" : "Password reset");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const onImpersonate = async () => {
    const ok = await confirm({
      title: isAr
        ? "ستدخل بصلاحيات مالك هذا Tenant. ستُستبدل جلستك الحالية مؤقتًا. للعودة استخدم زر Exit impersonation في الشريط العلوي."
        : "You will enter the tenant app as its owner. Your current admin session will be temporarily replaced. To return, use the Exit impersonation banner button.",
      confirmLabel: isAr ? "متابعة" : "Continue",
      cancelLabel: isAr ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/admin/tenants/${tenantId}/impersonate`);
      // Backend has already swapped the access_token cookie. We just
      // remember who we're impersonating so the Layout banner can render.
      localStorage.setItem("impersonating", JSON.stringify({
        tenant_id: tenantId,
        tenant_name: tenant?.name?.en || tenantId,
        owner_username: data.owner_username,
        owner_name: data.owner_name,
        expires_in_minutes: data.expires_in_minutes,
      }));
      toast.success(isAr ? `جارٍ الدخول كـ ${data.owner_username}…` : `Entering as ${data.owner_username}…`);
      // Hard-redirect so AuthContext re-bootstraps with the new cookie.
      window.location.href = "/dashboard";
    } catch (e) { toast.error(errMsg(e)); }
  };

  const onSaveEdit = async () => {
    await patch(editForm, isAr ? "تم حفظ التعديلات" : "Saved");
    setEditMode(false);
  };

  if (loading || !tenant) {
    return <div className="p-10 text-center text-stone-400 flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={16}/>Loading...</div>;
  }

  const usage = tenant.usage_month || {};
  const limits = tenant.limits || {};
  const counts = tenant.counts || {};
  const activity = tenant.activity || {};
  const plan = tenant.plan_summary || {};
  const owner = tenant.owner || {};

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-5" data-testid="tenant-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(backPath)} className="w-10 h-10 rounded-xl bg-white border border-stone-200 hover:border-stone-400 flex items-center justify-center text-stone-600">
            <ArrowLeft size={16} className={isAr ? "rotate-180" : ""}/>
          </button>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="font-heading text-2xl sm:text-3xl gold-text">{(tenant.name || {}).en || tenant.slug}</h1>
              <StatusBadge status={tenant.effective_status} lang={lang}/>
            </div>
            <div className="text-xs text-stone-400 font-mono">{tenant.tenant_id} · {tenant.slug}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!editMode ? (
            <button onClick={() => setEditMode(true)} className="px-4 py-2 rounded-xl bg-white border border-stone-200 hover:border-stone-400 text-sm flex items-center gap-2 text-stone-700" data-testid="edit-tenant">
              <Edit3 size={14}/>{isAr ? "تعديل" : "Edit"}
            </button>
          ) : (
            <>
              <button onClick={() => { setEditMode(false); load(); }} className="px-4 py-2 rounded-xl bg-white border border-stone-200 text-sm flex items-center gap-2 text-stone-600">
                <X size={14}/>{isAr ? "إلغاء" : "Cancel"}
              </button>
              <button onClick={onSaveEdit} className="btn-gold px-4 py-2 rounded-xl text-sm flex items-center gap-2" data-testid="save-tenant">
                <Save size={14}/>{isAr ? "حفظ" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-5">
          {/* Profile */}
          <Card title={isAr ? "بيانات النشاط" : "Profile"} Icon={Building2}>
            {editMode ? (
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-stone-500">Name (EN)</label><input className="input-dark mt-1" value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})}/></div>
                <div><label className="text-xs text-stone-500">Name (AR)</label><input className="input-dark mt-1" value={editForm.name_ar} onChange={e => setEditForm({...editForm, name_ar: e.target.value})}/></div>
                <div><label className="text-xs text-stone-500">{isAr ? "الدولة" : "Country"}</label><input className="input-dark mt-1" value={editForm.country} onChange={e => setEditForm({...editForm, country: e.target.value})}/></div>
                <div>
                  <label className="text-xs text-stone-500">{isAr ? "الحالة" : "Status"}</label>
                  <select className="input-dark mt-1" value={editForm.status} onChange={e => setEditForm({...editForm, status: e.target.value})}>
                    <option value="trial">{isAr ? "تجريبي" : "Trial"}</option>
                    <option value="active">{isAr ? "نشط" : "Active"}</option>
                    <option value="suspended">{isAr ? "موقوف" : "Suspended"}</option>
                    <option value="archived">{isAr ? "مؤرشف" : "Archived"}</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <Row label={isAr ? "النشاط (EN)" : "Name EN"} value={(tenant.name || {}).en}/>
                <Row label={isAr ? "النشاط (AR)" : "Name AR"} value={(tenant.name || {}).ar}/>
                <Row label={isAr ? "الدولة" : "Country"} value={tenant.country}/>
                <Row label={isAr ? "المدينة" : "City"} value={(tenant.metadata || {}).city}/>
                <Row label={isAr ? "تاريخ الإنشاء" : "Created"} value={tenant.created_at ? new Date(tenant.created_at).toLocaleString(isAr ? "ar-EG" : "en-US") : "—"}/>
                <Row label={isAr ? "آخر تحديث" : "Last update"} value={tenant.updated_at ? new Date(tenant.updated_at).toLocaleString(isAr ? "ar-EG" : "en-US") : "—"}/>
              </div>
            )}
          </Card>

          {/* Owner */}
          <Card title={isAr ? "بيانات المالك" : "Owner"} Icon={UsersIcon}>
            {editMode ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><label className="text-xs text-stone-500">{isAr ? "الاسم" : "Name"}</label><input className="input-dark mt-1" value={editForm.owner_name} onChange={e => setEditForm({...editForm, owner_name: e.target.value})}/></div>
                <div><label className="text-xs text-stone-500">{isAr ? "البريد" : "Email"}</label><input className="input-dark mt-1" value={editForm.owner_email} onChange={e => setEditForm({...editForm, owner_email: e.target.value})}/></div>
                <div><label className="text-xs text-stone-500">{isAr ? "الهاتف" : "Phone"}</label><input className="input-dark mt-1" value={editForm.owner_phone} onChange={e => setEditForm({...editForm, owner_phone: e.target.value})}/></div>
              </div>
            ) : (
              <div className="space-y-1">
                <Row label={isAr ? "الاسم" : "Name"} value={owner.name}/>
                <Row label={isAr ? "اسم المستخدم" : "Username"} value={owner.username} mono/>
                <Row label={isAr ? "البريد" : "Email"} value={owner.email}/>
                <Row label={isAr ? "الهاتف" : "Phone"} value={owner.phone}/>
              </div>
            )}
          </Card>

          {/* Branches */}
          <Card title={`${isAr ? "الفروع" : "Branches"} (${counts.branches || 0})`} Icon={MapPin}>
            {(tenant.branches || []).length === 0 ? (
              <div className="text-xs text-stone-400">{isAr ? "لا توجد فروع" : "No branches"}</div>
            ) : (
              <div className="space-y-2">
                {(tenant.branches || []).map(b => (
                  <div key={b.branch_id} className="flex items-center justify-between p-2.5 rounded-lg bg-stone-50 text-sm">
                    <div>
                      <div className="font-medium text-stone-800">{b.name}</div>
                      <div className="text-[10px] text-stone-400 font-mono">{b.branch_id}</div>
                    </div>
                    <div className="text-xs text-stone-500">{b.city || b.country || "—"}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Users */}
          <Card title={`${isAr ? "المستخدمون" : "Users"} (${counts.users || 0})`} Icon={UsersIcon}>
            {(tenant.users || []).length === 0 ? (
              <div className="text-xs text-stone-400">{isAr ? "لا يوجد مستخدمون" : "No users"}</div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-auto pe-1">
                {(tenant.users || []).map(u => (
                  <div key={u.user_id} className="flex items-center justify-between p-2.5 rounded-lg bg-stone-50 text-sm">
                    <div>
                      <div className="font-medium text-stone-800">{u.name || u.username}</div>
                      <div className="text-[10px] text-stone-400">{u.username} · {u.role}</div>
                    </div>
                    <div className="text-xs text-stone-500">{u.email || u.phone || "—"}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-5">
          {/* Subscription */}
          <Card title={isAr ? "الاشتراك" : "Subscription"} Icon={Crown}>
            <div className="space-y-2">
              <Row label={isAr ? "الخطة" : "Plan"} value={`${plan.name_en || tenant.plan} (${plan.monthly_price || 0} QAR/mo)`}/>
              <Row label={isAr ? "الحالة" : "Status"} value={<StatusBadge status={tenant.effective_status} lang={lang}/>}/>
              {tenant.subscription_started_at && (
                <Row label={isAr ? "بداية الاشتراك" : "Subscription start"}
                  value={new Date(tenant.subscription_started_at).toLocaleDateString(isAr ? "ar-EG" : "en-US")}/>
              )}
              {tenant.subscription_ends_at && (() => {
                const end = new Date(tenant.subscription_ends_at);
                const daysLeft = Math.ceil((end - new Date()) / (24 * 60 * 60 * 1000));
                const expired = daysLeft < 0;
                const tone = expired ? "text-rose-600 font-bold"
                  : daysLeft <= 7 ? "text-amber-600 font-semibold"
                  : "text-stone-700";
                return (
                  <Row
                    label={isAr ? "نهاية الاشتراك" : "Subscription end"}
                    value={
                      <span className={tone}>
                        {end.toLocaleDateString(isAr ? "ar-EG" : "en-US")}
                        <span className="text-xs ms-1 font-normal">
                          {expired
                            ? (isAr ? `(منتهي منذ ${-daysLeft} يوم)` : `(expired ${-daysLeft}d ago)`)
                            : (isAr ? `(${daysLeft} يوم متبقي)` : `(${daysLeft}d left)`)}
                        </span>
                      </span>
                    }
                  />
                );
              })()}
              {tenant.trial_ends_at && !tenant.subscription_ends_at && (
                <Row label={isAr ? "انتهاء التجربة" : "Trial ends"} value={new Date(tenant.trial_ends_at).toLocaleDateString(isAr ? "ar-EG" : "en-US")}/>
              )}
              <div className="pt-2">
                <button
                  onClick={() => setChangePlanOpen(true)}
                  className="btn-gold w-full py-2 rounded-lg flex items-center justify-center gap-2 text-sm"
                  data-testid="open-change-plan"
                >
                  <RefreshCw size={14}/>{isAr ? "تغيير الخطة / تجديد" : "Change plan / Renew"}
                </button>
              </div>
              <div className="border-t border-stone-100 mt-3 pt-3 space-y-3">
                <div>
                  <div className="text-xs text-stone-500 mb-1">{isAr ? "الفروع" : "Branches"}</div>
                  <ProgressBar value={counts.branches || 0} max={limits.max_branches !== undefined ? limits.max_branches : plan.max_branches}/>
                </div>
                <div>
                  <div className="text-xs text-stone-500 mb-1">{isAr ? "المستخدمون" : "Users"}</div>
                  <ProgressBar value={counts.users || 0} max={limits.max_users !== undefined ? limits.max_users : plan.max_users} color="#6366F1"/>
                </div>
                <div>
                  <div className="text-xs text-stone-500 mb-1">{isAr ? "الخدمات" : "Services"}</div>
                  <ProgressBar value={counts.services || 0} max={plan.max_services} color="#10B981"/>
                </div>
              </div>
            </div>
          </Card>

          {/* Usage / Activity */}
          <Card title={isAr ? "استخدام الشهر الحالي" : "This Month's Usage"} Icon={BarChart3}>
            <div className="space-y-3">
              <div>
                <div className="text-xs text-stone-500 mb-1">{isAr ? "الحجوزات" : "Bookings"}</div>
                <ProgressBar value={usage.bookings || 0} max={usage.limit_bookings || 1} color="#F59E0B"/>
              </div>
              <div className="grid grid-cols-2 gap-2 text-center mt-2">
                <div className="bg-stone-50 rounded-lg p-2">
                  <div className="text-[10px] text-stone-400 uppercase tracking-wider">{isAr ? "الفواتير" : "Invoices"}</div>
                  <div className="text-lg font-bold text-stone-800 num-display">{usage.invoices || 0}</div>
                </div>
                <div className="bg-stone-50 rounded-lg p-2">
                  <div className="text-[10px] text-stone-400 uppercase tracking-wider">{isAr ? "الإيراد (ر.ق)" : "Revenue (QAR)"}</div>
                  <div className="text-lg font-bold text-stone-800 num-display">{(usage.revenue || 0).toFixed(0)}</div>
                </div>
              </div>
            </div>
          </Card>

          {/* Activity — operationally critical signals: is this tenant alive? */}
          <Card title={isAr ? "سجل النشاط" : "Activity Log"} Icon={Activity}>
            <div className="space-y-1">
              <Row label={isAr ? "تاريخ الإنشاء" : "Created"} value={tenant.created_at ? new Date(tenant.created_at).toLocaleString(isAr ? "ar-EG" : "en-US") : "—"}/>
              <Row label={isAr ? "آخر دخول" : "Last login"} value={activity.last_login ? new Date(activity.last_login).toLocaleString(isAr ? "ar-EG" : "en-US") : "—"}/>
              <Row label={isAr ? "آخر نشاط" : "Last activity"} value={activity.last_activity ? new Date(activity.last_activity).toLocaleString(isAr ? "ar-EG" : "en-US") : "—"}/>
              <Row label={isAr ? "آخر حجز" : "Last booking"} value={activity.last_booking_at ? new Date(activity.last_booking_at).toLocaleString(isAr ? "ar-EG" : "en-US") : "—"}/>
              <Row label={isAr ? "آخر فاتورة" : "Last invoice"} value={activity.last_invoice_at ? new Date(activity.last_invoice_at).toLocaleString(isAr ? "ar-EG" : "en-US") : "—"}/>
              <Row label={isAr ? "مستخدمون نشطون (30 يوم)" : "Active users (30d)"} value={(activity.active_users_30d ?? 0)}/>
              <Row label={isAr ? "حجم التخزين" : "Storage"} value={activity.storage_mb != null ? `${activity.storage_mb} MB` : "—"}/>
            </div>
          </Card>

          {/* Quick Actions */}
          <Card title={isAr ? "إجراءات سريعة" : "Quick Actions"} Icon={Database}>
            <div className="space-y-2">
              <ActionBtn Icon={LogIn} label={isAr ? "الدخول كمالك" : "Login as owner"} onClick={onImpersonate} variant="gold" data-testid="action-impersonate"/>
              <ActionBtn Icon={Crown} label={isAr ? "تغيير الخطة" : "Change plan"} onClick={onChangePlan} data-testid="action-change-plan"/>
              <ActionBtn Icon={RefreshCw} label={isAr ? "تمديد التجربة" : "Extend trial"} onClick={onExtendTrial} data-testid="action-extend-trial"/>
              <ActionBtn Icon={Key} label={isAr ? "إعادة كلمة السر" : "Reset owner password"} onClick={onResetPassword} data-testid="action-reset-pwd"/>
              {tenant.effective_status === "suspended" || tenant.effective_status === "archived" ? (
                <ActionBtn Icon={CheckCircle2} label={isAr ? "إعادة التفعيل" : "Reactivate"} onClick={onReactivate} variant="success" data-testid="action-reactivate"/>
              ) : (
                <ActionBtn Icon={PauseCircle} label={isAr ? "تعليق" : "Suspend"} onClick={onSuspend} variant="danger" data-testid="action-suspend"/>
              )}
              <ActionBtn Icon={Archive} label={isAr ? "أرشفة" : "Archive"} onClick={onArchive} variant="danger" data-testid="action-archive"/>
            </div>
          </Card>
        </div>
      </div>

      <ChangePlanModal
        open={changePlanOpen}
        tenant={tenant}
        onClose={() => setChangePlanOpen(false)}
        onSaved={load}
        lang={lang}
      />
      <ConfirmDialogSlot />
    </div>
  );
}
